import type { QuestionAnswerReview, ResultReviewSummary, ResultSectionScore, TestReviewData } from '@/features/results/result-detail-api'
import {
  CRITERIA, DEMO_CHART_URL, DEMO_ESSAYS, criteriaEvidence, essayText, languageObservations, taskBand, wordCount,
  type EssayContent, type TaskBands, type TaskNumber,
} from '@/features/results/demo-writing-content'
import { demoFullSections } from '@/lib/demo-test-definitions'
import type { Section, TestDefinition, TestResult } from '@/lib/types'

/**
 * Sample results for the demo session (fictional student "Noura A."). Each sample is a `/review/` payload built from the real
 * server serializer shapes (scout/backend-results.md): `answers.by_question[]` essay rows with `details.criteria`,
 * `details.language_observations`, `user_answer` and `word_count`; `section_scores.writing` with `task_bands`,
 * `band_calculation` and the examiner projection; Speaking `part_breakdown` and HTML `feedback`. The list rows
 * (`demoSampleResults`) are derived from the same summaries so the two can never disagree.
 *
 * Ids (open at `/results/<id>`):
 *   demo-writing-published   Writing, both tasks marked, Published and reviewed by a teacher. Feedback ready at once.
 *   demo-writing-indicative  Writing, same essays, Indicative. Feedback is pending for two polls, then ready.
 *   demo-writing-fallback    Writing, Published (no teacher review). The feedback endpoint answers 404: client fallback.
 *   demo-writing-legacy      Writing marked before the evidence existed: bands only. Feedback ready with no annotations.
 *   demo-speaking-result     Speaking, three parts with feedback, Published.
 *   demo-result-marking      Being marked (queued a few minutes before the page loaded, so no "taking longer" line).
 *   demo-result-failed       Could not mark (failed).
 *   demo-result-pending      Being marked, queued in July: the delayed variant ("taking longer than usual").
 *   demo-result-review       A full mock waiting for a teacher (needs_review): Being marked, Listening and Reading bands already there.
 *   demo-result-full-mock    Full mock, four skills, Writing is the lowest, Published, for the readiness view.
 *   demo-result-listening-zero  Listening with a raw score of 0: shows the raw score and "No band yet", never 0.0.
 * Existing samples keep their ids and shapes: demo-result, demo-result-pending (a long-delayed marking),
 * demo-result-review (needs_review), demo-result-abandoned, demo-result-not-attempted.
 */

const WRITING_SECTION_ID = 'df-writing'
const writingSection = demoFullSections.find((section) => section.type === 'Writing')!
const speakingSection = demoFullSections.find((section) => section.type === 'Speaking')!
const listeningSection = demoFullSections.find((section) => section.type === 'Listening')!

/** The Writing section with the Task 1 chart attached the way the server sends it (`type_data.image_url` + the part's media). */
function chartedSection(section: Section): Section {
  return {
    ...section,
    parts: section.parts.map((part) => ({
      ...part,
      media_resources: [{ id: 'demo-chart-recycling', media_type: 'image', file_path: DEMO_CHART_URL, description: 'Bar chart showing household recycling rates in four countries, 2005, 2015 and 2025' }],
      question_groups: part.question_groups.map((group) => ({
        ...group,
        questions: group.questions.map((question) => question.number === 1
          ? { ...question, type_data: { ...question.type_data, image_url: DEMO_CHART_URL, task_number: 1 } }
          : { ...question, type_data: { ...question.type_data, task_number: 2 } }),
      })),
    })),
  } as Section
}

const questionText = (taskNumber: TaskNumber) =>
  writingSection.parts[0]!.question_groups[0]!.questions.find((question) => question.number === taskNumber)!.text

function testStructure(id: string, title: string, types: TestDefinition['types'], sections: Section[], timeLimit: number): TestReviewData['test_structure'] {
  return { id, title, display_title: title, description: `Sample result for ${title}.`, types, level: 'Intermediate', time_limit: timeLimit, is_active: true, sections }
}

/* -------------------------------------------------------------------------- */
/* Bands                                                                       */
/* -------------------------------------------------------------------------- */

const bandsOf = (ta: number, cc: number, lr: number, gra: number): TaskBands =>
  ({ task_achievement: ta, coherence_cohesion: cc, lexical_resource: lr, grammatical_range: gra })

/** Task 1 5.75, Task 2 6.0, Writing 6.0 (quarter task means are real: the server sends them unrounded). */
export const BANDS_WRITING: Record<TaskNumber, TaskBands> = { 1: bandsOf(6, 6, 5.5, 5.5), 2: bandsOf(6.5, 6, 6, 5.5) }
/** The full mock's Writing: Task 1 5.5, Task 2 5.625, Writing 5.5, the lowest skill. */
export const BANDS_MOCK: Record<TaskNumber, TaskBands> = { 1: bandsOf(5.5, 5.5, 5.5, 5.5), 2: bandsOf(6, 5.5, 5.5, 5.5) }
/** Marked before the criterion evidence existed: bands only. */
export const BANDS_LEGACY: Record<TaskNumber, TaskBands> = { 1: bandsOf(5.5, 5.5, 5.5, 5.5), 2: bandsOf(5.5, 5.5, 5.5, 5.5) }

/** Writing counts Task 2 twice, then rounds to the nearest half band (`writing-single-round-v1`). */
export const halfBand = (value: number) => Math.floor(value * 2 + 0.5) / 2
export const writingBand = (bands: Record<TaskNumber, TaskBands>) => halfBand((taskBand(bands[1]) + 2 * taskBand(bands[2])) / 3)

/* -------------------------------------------------------------------------- */
/* Essay rows                                                                  */
/* -------------------------------------------------------------------------- */

const LABELS = (taskNumber: TaskNumber): Record<string, string> => ({
  task_achievement: taskNumber === 2 ? 'Task response' : 'Task achievement',
  coherence_cohesion: 'Coherence and cohesion',
  lexical_resource: 'Lexical resource',
  grammatical_range: 'Grammatical range and accuracy',
})

type RowMode = 'marked' | 'legacy' | 'queued' | 'failed'

function essayRow(content: EssayContent, bands: TaskBands, mode: RowMode): QuestionAnswerReview {
  const text = essayText(content)
  const labels = LABELS(content.taskNumber)
  const base: QuestionAnswerReview = {
    question_id: content.questionId,
    question_number: content.taskNumber,
    question_text: questionText(content.taskNumber),
    question_type: 'essay',
    part_id: 'df-w-p1',
    part_order: 1,
    part_name: 'Writing tasks',
    user_answer: text,
    correct_answers: null,
    max_score: 9,
  }
  if (mode === 'queued') return { ...base, score_earned: null, is_fully_correct: false, is_partially_correct: false, feedback: '', details: { task_number: content.taskNumber } }
  if (mode === 'failed') {
    return {
      ...base, score_earned: null, is_fully_correct: false, is_partially_correct: false,
      feedback: 'Writing assessment could not be completed.',
      details: { task_number: content.taskNumber, ai_error: 'Writing assessment could not be completed.', error_code: 'writing_grading_failed', assessment_status: 'failed', retryable: false },
    }
  }
  const band = taskBand(bands)
  const areas = CRITERIA.filter((criterion) => bands[criterion] < 7).map((criterion) => `${labels[criterion]}: ${content.judgement[criterion]}`).join(' ')
  const common = {
    task_number: content.taskNumber,
    band,
    word_count: wordCount(text),
    task_achievement: bands.task_achievement,
    coherence_cohesion: bands.coherence_cohesion,
    lexical_resource: bands.lexical_resource,
    grammatical_range: bands.grammatical_range,
    feedback_strengths: '',
    feedback_areas_for_improvement: areas,
    feedback_recommendations: content.recommendations,
  }
  const details = mode === 'legacy'
    ? common
    : {
        ...common,
        criteria: criteriaEvidence(content, bands),
        language_observations: languageObservations(content),
        ...(content.visualDataExtracted ? { visual_data_extracted: content.visualDataExtracted } : {}),
        off_topic_content: '',
        relevance_score: 0.95,
        rulebook_penalties: { word_count: { points: 0, reason: '', deviation_percent: 0 }, relevance: { points: 0, reason: '', flagged_for_review: false, score_effect: 'none' } },
        // Technical fields that really do reach the browser. The student pages must never render them.
        criterion_independence: { halo_detected: false, warning: '', evidence_status: 'verified', validation_problems: [], flagged_for_review: false },
        grading_provenance: { provider: 'sample', model: 'sample-grader-model', grading_method: 'language-observation-v1', reasoning_effort: 'medium' },
      }
  return {
    ...base,
    score_earned: Math.round(band * 100) / 100,
    is_fully_correct: band >= 7,
    is_partially_correct: band >= 5 && band < 7,
    feedback: `Areas for improvement: ${areas}\n\nRecommendations: ${content.recommendations}`,
    details,
  }
}

const essayRows = (bands: Record<TaskNumber, TaskBands>, mode: RowMode) => [essayRow(DEMO_ESSAYS[1], bands[1], mode), essayRow(DEMO_ESSAYS[2], bands[2], mode)]

/* -------------------------------------------------------------------------- */
/* Section scores                                                              */
/* -------------------------------------------------------------------------- */

type Examiner = 'reviewed' | 'indicative' | 'none'
const REVIEWED_AT = '2026-10-06T08:40:00Z'

const examinerFields = (examiner: Examiner): Partial<ResultSectionScore> =>
  examiner === 'reviewed'
    ? { examiner_verified: true, examiner_reviewed_at: REVIEWED_AT, score_status: 'confirmed', review_pending: false, band_changed_on_review: false }
    : examiner === 'indicative'
      ? { examiner_verified: false, examiner_reviewed_at: null, score_status: 'indicative', review_pending: true, band_changed_on_review: false }
      : {}

function writingScore(bands: Record<TaskNumber, TaskBands>, examiner: Examiner, scale: 'writing-only' | 'mock'): ResultSectionScore {
  const t1 = taskBand(bands[1])
  const t2 = taskBand(bands[2])
  const band = writingBand(bands)
  const score = scale === 'writing-only' ? band * 2 : band
  const max = scale === 'writing-only' ? 18 : 9
  // Real server field, not typed on ResultSectionScore because no page reads it.
  const calculation = { band_calculation: { version: 'writing-single-round-v1', task_inputs: { task_1: [{ value: t1, basis: 'criterion_mean' }], task_2: [{ value: t2, basis: 'criterion_mean' }] }, weights: { task_1: 1, task_2_and_later_mean: 2 }, rounding: 'nearest_half_up_after_weighting', band } }
  return {
    score, max_score: max, percentage: Math.round((score / max) * 10_000) / 100, band,
    task_bands: { task_1: t1, task_2: t2 },
    ...calculation,
    ...examinerFields(examiner),
  }
}

/* -------------------------------------------------------------------------- */
/* Speaking                                                                    */
/* -------------------------------------------------------------------------- */

const SPEAKING_FEEDBACK = `<p><strong>Strengths:</strong> You answer in full sentences and keep going without long pauses. Your ideas about your neighbourhood and your cooking habits are easy to follow.</p><p><strong>Areas for improvement:</strong> Some answers in Part 3 stay general. Add a reason and an example to each opinion. A few word endings are dropped, for example plural and past forms.</p><p><strong>Tips:</strong></p><ul><li>Give a reason and an example for every opinion in Part 3.</li><li>Practise the past tense with a short story about a place that changed.</li><li>Fill the full two minutes in Part 2 with a simple structure: before, now, why.</li></ul><p><em>Your pronunciation is clear. Stress on longer words is sometimes unusual, but it rarely blocks meaning.</em></p>`
const PART3_NOTE = `<p>Your opinions are clear, but each one needs a reason. Try "because" plus one example from your own city.</p>`

const speakingSeconds: Record<string, number> = { 'df-s-q1': 38, 'df-s-q2': 41, 'df-s-q3': 29, 'df-s-q4': 112, 'df-s-q5': 44, 'df-s-q6': 47, 'df-s-q7': 41 }

function speakingRows(): QuestionAnswerReview[] {
  return speakingSection.parts.flatMap((part, partIndex) => part.question_groups.flatMap((group) => group.questions.map((question) => ({
    question_id: question.id,
    question_number: question.number,
    question_text: question.text,
    question_type: question.type,
    part_id: part.id,
    part_order: partIndex + 1,
    part_name: part.name ?? `Part ${partIndex + 1}`,
    user_answer: { media_id: `demo-${question.id}`, duration_seconds: speakingSeconds[question.id] ?? 30 },
    // Sample audio is not served in the demo session: the player shows the honest "could not be loaded" line.
    details: { media_id: `demo-${question.id}`, audio_url: `/mocktest/media/demo-${question.id}/`, assessment_status: 'succeeded' },
    score_earned: null,
    max_score: 0,
    is_fully_correct: false,
    is_partially_correct: false,
    feedback: partIndex === 2 && question.number === 5 ? PART3_NOTE : '',
  }))))
}

/** Sample part bands (the server sends one per part, ticket #219). */
const SPEAKING_PART_BANDS = [6.5, 6.5, 6.0]

function speakingScore(band: number, examiner: Examiner): ResultSectionScore {
  const parts = speakingSection.parts.map((part, index) => {
    const questions = part.question_groups.flatMap((group) => group.questions)
    return {
      part_id: part.id, part_label: part.name ?? `Part ${index + 1}`, band: SPEAKING_PART_BANDS[index] ?? null,
      recording_time_seconds: questions.reduce((sum, question) => sum + (speakingSeconds[question.id] ?? 30), 0),
      unrecorded_questions: 0, total_questions: questions.length,
    }
  })
  return {
    score: band, max_score: 9, percentage: Math.round((band / 9) * 10_000) / 100, band,
    part_breakdown: parts,
    feedback: SPEAKING_FEEDBACK,
    assessment_status: 'succeeded',
    ...examinerFields(examiner),
  }
}

/* -------------------------------------------------------------------------- */
/* Summaries                                                                   */
/* -------------------------------------------------------------------------- */

function summary(id: string, fields: Partial<ResultReviewSummary> & Pick<ResultReviewSummary, 'completed_at' | 'section_scores'>): ResultReviewSummary {
  return {
    id, progress_id: null, usefulness_feedback: null,
    score: null, max_score: null, percentage: null, total_questions: 2, questions_correct: null,
    time_spent: 3_540, attempt_number: 1, tab_switch_count: 0, status: 'completed',
    grading_status: 'succeeded', score_publication_status: 'published', timing_mode: 'timed',
    test_available: true, can_retake: true, band_status: 'published',
    ...fields,
  }
}

function writingReview(id: string, title: string, fields: { completedAt: string; bands: Record<TaskNumber, TaskBands>; examiner: Examiner; mode?: RowMode; bandStatus: 'published' | 'confirmed' | 'indicative' }): TestReviewData {
  const section = writingScore(fields.bands, fields.examiner, 'writing-only')
  return {
    test_structure: testStructure('demo-writing', title, ['Writing'], [chartedSection(writingSection)], 60),
    result_summary: summary(id, {
      completed_at: fields.completedAt, grading_queued_at: fields.completedAt,
      score: section.score ?? null, max_score: 18, percentage: section.percentage ?? null, questions_correct: 0,
      section_timings: { [WRITING_SECTION_ID]: 3_540 }, display_time_spent: 3_540, display_section_timings: { [WRITING_SECTION_ID]: 3_540 },
      band_status: fields.bandStatus,
      section_scores: { writing: section, overall_band: section.band ?? null },
    }),
    answers: { by_question: essayRows(fields.bands, fields.mode ?? 'marked') },
    grading_status: 'succeeded',
    score_publication_status: 'published',
  }
}

const MARKING_QUEUED_AT = new Date(Date.now() - 4 * 60_000).toISOString()

function withheldWriting(id: string, title: string, mode: 'queued' | 'failed', fields: { completedAt: string }): TestReviewData {
  const failed = mode === 'failed'
  return {
    test_structure: testStructure('demo-writing', title, ['Writing'], [chartedSection(writingSection)], 60),
    result_summary: summary(id, {
      completed_at: fields.completedAt, grading_queued_at: fields.completedAt, questions_correct: null, total_questions: 2,
      grading_status: failed ? 'failed' : 'running', score_publication_status: 'not_applicable', band_status: null,
      ...(failed ? { error_code: 'writing_grading_failed' } : {}),
      section_scores: { writing: { band: null, score: null, max_score: 18 }, overall_band: null },
    }),
    answers: { by_question: essayRows(BANDS_WRITING, mode) },
    grading_status: failed ? 'failed' : 'running',
    score_publication_status: 'not_applicable',
  }
}

function speakingReview(): TestReviewData {
  const section = speakingScore(6.5, 'none')
  return {
    test_structure: testStructure('demo-speaking', 'Speaking Practice 05', ['Speaking'], [speakingSection], 14),
    result_summary: summary('demo-speaking-result', {
      completed_at: '2026-10-07T12:05:00Z', grading_queued_at: '2026-10-07T12:05:00Z',
      score: 6.5, max_score: 9, percentage: 72.22, total_questions: 7, questions_correct: 0, time_spent: 840,
      section_timings: { 'df-speaking': 840 }, display_time_spent: 840, display_section_timings: { 'df-speaking': 840 },
      section_scores: { speaking: section, overall_band: 6.5 },
    }),
    answers: { by_question: speakingRows() },
    grading_status: 'succeeded',
    score_publication_status: 'published',
  }
}

const MOCK_SECTION_SECONDS: Record<string, number> = { 'df-listening': 1_900, 'df-reading': 3_540, 'df-writing': 3_540, 'df-speaking': 840 }

function fullMockReview(): TestReviewData {
  const writing = writingScore(BANDS_MOCK, 'none', 'mock')
  const speaking = speakingScore(6, 'none')
  const sections = demoFullSections.map((section) => (section.type === 'Writing' ? chartedSection(section) : section))
  return {
    test_structure: testStructure('demo-full', 'Academic Mock 05', ['Listening', 'Reading', 'Writing', 'Speaking'], sections, 164),
    result_summary: summary('demo-result-full-mock', {
      completed_at: '2026-09-30T10:05:00Z', grading_queued_at: '2026-09-30T10:05:00Z',
      score: 61.5, max_score: 98, percentage: 62.76, total_questions: 80, questions_correct: 50, time_spent: 9_820, attempt_number: 1,
      section_timings: MOCK_SECTION_SECONDS, display_time_spent: 9_820, display_section_timings: MOCK_SECTION_SECONDS,
      section_scores: {
        listening: { band: 6.5, score: 26, max_score: 40, percentage: 65 },
        reading: { band: 6, score: 24, max_score: 40, percentage: 60 },
        writing,
        speaking,
        overall_band: 6,
      },
    }),
    answers: { by_question: [...essayRows(BANDS_MOCK, 'marked'), ...speakingRows()] },
    grading_status: 'succeeded',
    score_publication_status: 'published',
  }
}

function listeningZeroReview(): TestReviewData {
  return {
    test_structure: testStructure('demo-listening', 'Listening Practice 03', ['Listening'], [listeningSection], 40),
    result_summary: summary('demo-result-listening-zero', {
      completed_at: '2026-10-03T15:30:00Z', score: 0, max_score: 40, percentage: 0, total_questions: 40, questions_correct: 0, time_spent: 2_240,
      section_scores: { listening: { score: 0, max_score: 40, percentage: 0, band: null }, overall_band: null },
      band_status: null,
    }),
    answers: { by_question: [] },
    grading_status: 'succeeded',
    score_publication_status: 'published',
  }
}

/* -------------------------------------------------------------------------- */
/* Registry                                                                    */
/* -------------------------------------------------------------------------- */

interface Sample { review: TestReviewData; testId: string; title: string; types: TestDefinition['types'] }

const sample = (review: TestReviewData): Sample => ({ review, testId: review.test_structure.id, title: review.test_structure.title, types: review.test_structure.types })

/** Newest first, so the dashboard and history show the samples at the top. */
const SAMPLES: Sample[] = [
  sample(withheldWriting('demo-result-marking', 'Academic Writing Practice 14', 'queued', { completedAt: MARKING_QUEUED_AT })),
  sample(writingReview('demo-writing-indicative', 'Academic Writing Practice 13', { completedAt: '2026-10-08T16:20:00Z', bands: BANDS_WRITING, examiner: 'indicative', bandStatus: 'indicative' })),
  sample(withheldWriting('demo-result-failed', 'Academic Writing Practice 15', 'failed', { completedAt: '2026-10-09T18:10:00Z' })),
  sample(speakingReview()),
  sample(writingReview('demo-writing-published', 'Academic Writing Practice 12', { completedAt: '2026-10-05T09:12:00Z', bands: BANDS_WRITING, examiner: 'reviewed', bandStatus: 'confirmed' })),
  sample(listeningZeroReview()),
  sample(writingReview('demo-writing-fallback', 'Academic Writing Practice 11', { completedAt: '2026-10-02T07:45:00Z', bands: BANDS_WRITING, examiner: 'none', bandStatus: 'published' })),
  sample(fullMockReview()),
  sample(writingReview('demo-writing-legacy', 'Academic Writing Practice 06', { completedAt: '2026-06-14T10:30:00Z', bands: BANDS_LEGACY, examiner: 'none', mode: 'legacy', bandStatus: 'published' })),
]

/** Review payloads for the two older list rows that had no review sample of their own (their list rows stay in demo-data.ts). */
function needsReviewReview(): TestReviewData {
  const sections = demoFullSections.map((section) => (section.type === 'Writing' ? chartedSection(section) : section))
  return {
    test_structure: testStructure('demo-full', 'Academic Mock 02', ['Listening', 'Reading', 'Writing', 'Speaking'], sections, 164),
    result_summary: summary('demo-result-review', {
      completed_at: '2026-07-21T09:40:00Z', grading_queued_at: '2026-07-21T09:40:00Z', score: 52, max_score: 80, percentage: 65, total_questions: 80, questions_correct: 52,
      time_spent: 9_320, attempt_number: 2, tab_switch_count: 3, grading_status: 'needs_review', score_publication_status: 'withheld', band_status: null,
      section_scores: {
        listening: { band: 6.5, score: 27, max_score: 40, percentage: 67.5 },
        reading: { band: 6, score: 25, max_score: 40, percentage: 62.5 },
        writing: { band: null, score: null, max_score: 9 },
        overall_band: null,
      },
    }),
    answers: { by_question: essayRows(BANDS_WRITING, 'queued') },
    grading_status: 'needs_review',
    score_publication_status: 'withheld',
  }
}

const REVIEWS = new Map<string, TestReviewData>([
  ...SAMPLES.map((entry): [string, TestReviewData] => [entry.review.result_summary.id, entry.review]),
  // The long-delayed marking row (queued in July): exercises the "taking longer than usual" line.
  ['demo-result-pending', withheldWriting('demo-result-pending', 'Writing Coherence Diagnostic', 'queued', { completedAt: '2026-07-29T18:15:00Z' })],
  ['demo-result-review', needsReviewReview()],
])

/** The sample `/review/` payload for a demo result id, or undefined when this module has no sample for it. */
export function demoSampleReview(resultId: string): TestReviewData | undefined {
  return REVIEWS.get(resultId)
}

function listRow({ review, testId, title, types }: Sample): TestResult {
  const s = review.result_summary
  const writing = s.section_scores.writing
  const pending = s.grading_status === 'running' || s.grading_status === 'failed'
  return {
    id: s.id, test_id: testId, test_title: title, test_types: types,
    score: pending ? 0 : s.score ?? 0, max_score: s.max_score ?? 0, percentage_score: pending ? 0 : s.percentage ?? 0,
    completed_at: s.completed_at, time_spent: s.time_spent, total_questions: s.total_questions,
    section_timings: s.section_timings, display_time_spent: s.display_time_spent, display_section_timings: s.display_section_timings,
    attempt_number: s.attempt_number, tab_switch_count: s.tab_switch_count, status: s.status, timing_mode: s.timing_mode,
    test_available: true, can_retake: !(s.grading_status === 'running'),
    grading_status: s.grading_status, score_publication_status: s.score_publication_status, grading_error_code: s.error_code ?? null,
    ...(s.band_status !== undefined ? { band_status: s.band_status } : {}),
    section_scores: pending && writing ? { writing: { score: 0, max_score: 18 } } : (s.section_scores as TestResult['section_scores']),
  } as TestResult
}

/** List rows for the demo history, dashboard and results list. Derived from the review summaries above. */
export const demoSampleResults: TestResult[] = SAMPLES.map(listRow)
